import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";

// Beast V1 Retainer Engine — Customer detail sub-panel.
//
// Rendered ONLY when the parent Customer's `commercial_mode="retainer"`.
// Composes three compact blocks:
//   1. Retainer Terms (editable form)
//   2. Current Retainer (live cycle summary)
//   3. Retainer History (last closed cycles)
//
// Zero references to Hilton or any specific values — every rendered
// number comes from the API. Job Drawer is untouched.

const fmtMoney = (v, currency = "AWG") =>
  `${currency} ${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtHours = (v) => `${Number(v || 0).toFixed(2)}h`;

const fmtDateRange = (start, end) => {
  const opts = { month: "short", day: "numeric" };
  const s = new Date(start).toLocaleDateString("en-US", opts);
  const e = new Date(end).toLocaleDateString("en-US", opts);
  return `${s} – ${e}`;
};

const emptyTerms = {
  monthly_retainer_fee: "",
  included_hours: "",
  overage_hourly_rate: "",
  billing_cycle_start_day: "",
  billing_cycle_end_day: "",
  currency: "AWG",
};

export const RetainerPanel = ({ customerId, initialTerms, onChanged }) => {
  const [termsDraft, setTermsDraft] = useState(
    initialTerms
      ? {
          monthly_retainer_fee: String(initialTerms.monthly_retainer_fee ?? ""),
          included_hours: String(initialTerms.included_hours ?? ""),
          overage_hourly_rate: String(initialTerms.overage_hourly_rate ?? ""),
          billing_cycle_start_day: String(initialTerms.billing_cycle_start_day ?? ""),
          billing_cycle_end_day: String(initialTerms.billing_cycle_end_day ?? ""),
          currency: initialTerms.currency || "AWG",
        }
      : { ...emptyTerms }
  );
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/customers/${customerId}/retainer`
      );
      setCurrent(data);
    } catch (e) {
      // 422 means Customer hasn't been given terms yet — this is
      // an EXPECTED first-time state, not an error to shout about.
      if (e?.response?.status !== 422) {
        toast.error(e?.response?.data?.detail || "Failed to load retainer");
      }
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/admin/customers/${customerId}/retainer/history?count=6`
      );
      setHistory(data?.cycles || []);
    } catch {
      setHistory([]);
    }
  }, [customerId]);

  useEffect(() => {
    loadCurrent();
    loadHistory();
  }, [loadCurrent, loadHistory]);

  const saveTerms = async () => {
    setBusy(true);
    try {
      const payload = {
        monthly_retainer_fee: parseFloat(termsDraft.monthly_retainer_fee),
        included_hours: parseFloat(termsDraft.included_hours),
        overage_hourly_rate: parseFloat(termsDraft.overage_hourly_rate),
        billing_cycle_start_day: parseInt(termsDraft.billing_cycle_start_day, 10),
        billing_cycle_end_day: parseInt(termsDraft.billing_cycle_end_day, 10),
        currency: (termsDraft.currency || "AWG").toUpperCase(),
      };
      for (const [k, v] of Object.entries(payload)) {
        if (k === "currency") continue;
        if (Number.isNaN(v)) {
          toast.error(`Field ${k} must be a number`);
          setBusy(false);
          return;
        }
      }
      await api.patch(`/admin/customers/${customerId}/retainer-terms`, payload);
      toast.success("Retainer Terms saved");
      await loadCurrent();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save terms");
    } finally {
      setBusy(false);
    }
  };

  const closeCycle = async () => {
    if (!current?.active_cycle) return;
    if (!confirm(
      `Close cycle ${fmtDateRange(current.active_cycle.cycle_start, current.active_cycle.cycle_end)}?\n` +
      `Freezes: ${fmtHours(current.active_cycle.chargeable_hours)} chargeable, ` +
      `overage ${fmtHours(current.active_cycle.overage_hours)}. Once closed, ` +
      `this snapshot cannot be edited from the UI.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/admin/customers/${customerId}/retainer/close-cycle`);
      toast.success("Cycle closed");
      await loadCurrent();
      await loadHistory();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close cycle");
    } finally {
      setBusy(false);
    }
  };

  const active = current?.active_cycle;
  const alreadyClosed = !!current?.closed_snapshot_id;

  return (
    <div className="space-y-5" data-testid="retainer-panel">
      {/* --- Retainer Terms editor -------------------------------- */}
      <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-3">
        <div className="ur-eyebrow mb-3">Retainer Terms</div>
        <div className="grid grid-cols-2 gap-3 text-xs text-[#A3A3A3]">
          <label>
            Monthly Retainer Fee
            <input
              data-testid="retainer-monthly-fee"
              className="ur-input mt-1 w-full text-sm"
              type="number" step="0.01" min="0"
              value={termsDraft.monthly_retainer_fee}
              onChange={(e) => setTermsDraft({ ...termsDraft, monthly_retainer_fee: e.target.value })}
            />
          </label>
          <label>
            Included Hours
            <input
              data-testid="retainer-included-hours"
              className="ur-input mt-1 w-full text-sm"
              type="number" step="0.01" min="0.01"
              value={termsDraft.included_hours}
              onChange={(e) => setTermsDraft({ ...termsDraft, included_hours: e.target.value })}
            />
          </label>
          <label>
            Currency
            <input
              data-testid="retainer-currency"
              className="ur-input mt-1 w-full text-sm"
              maxLength={4}
              value={termsDraft.currency}
              onChange={(e) => setTermsDraft({ ...termsDraft, currency: e.target.value })}
            />
          </label>
          <label>
            Overage Hourly Rate
            <input
              data-testid="retainer-overage-rate"
              className="ur-input mt-1 w-full text-sm"
              type="number" step="0.01" min="0"
              value={termsDraft.overage_hourly_rate}
              onChange={(e) => setTermsDraft({ ...termsDraft, overage_hourly_rate: e.target.value })}
            />
          </label>
          <label>
            Billing Cycle Start Day
            <input
              data-testid="retainer-cycle-start"
              className="ur-input mt-1 w-full text-sm"
              type="number" min="1" max="31"
              value={termsDraft.billing_cycle_start_day}
              onChange={(e) => setTermsDraft({ ...termsDraft, billing_cycle_start_day: e.target.value })}
            />
          </label>
          <label>
            Billing Cycle End Day
            <input
              data-testid="retainer-cycle-end"
              className="ur-input mt-1 w-full text-sm"
              type="number" min="1" max="31"
              value={termsDraft.billing_cycle_end_day}
              onChange={(e) => setTermsDraft({ ...termsDraft, billing_cycle_end_day: e.target.value })}
            />
          </label>
        </div>
        {/* Effective included rate — derived, read-only. */}
        {termsDraft.monthly_retainer_fee && termsDraft.included_hours && Number(termsDraft.included_hours) > 0 && (
          <div className="mt-2 text-[10px] text-[#7A7A7A]" data-testid="retainer-effective-rate">
            Effective Included Rate: {(termsDraft.currency || "AWG").toUpperCase()} {(
              Number(termsDraft.monthly_retainer_fee) / Number(termsDraft.included_hours)
            ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hr (calculated)
          </div>
        )}
        <div className="flex justify-end mt-3">
          <button
            data-testid="retainer-save-terms"
            onClick={saveTerms}
            disabled={busy}
            className="ur-btn-primary text-xs py-2 px-3"
          >
            Save Retainer Terms
          </button>
        </div>
      </div>

      {/* --- Current Cycle ---------------------------------------- */}
      {loading ? (
        <div className="text-xs text-[#A3A3A3] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading retainer…
        </div>
      ) : active ? (
        <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-3" data-testid="retainer-current">
          <div className="flex items-center justify-between mb-2">
            <div className="ur-eyebrow">Current Retainer</div>
            {alreadyClosed && (
              <span className="text-[10px] text-[#A3A3A3] inline-flex items-center gap-1">
                <Lock className="w-3 h-3" /> closed
              </span>
            )}
          </div>
          <div className="text-xs text-[#A3A3A3] mb-2" data-testid="retainer-cycle-range">
            {fmtDateRange(active.cycle_start, active.cycle_end)}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[#A3A3A3] text-xs">Used</div>
              <div className="text-white" data-testid="retainer-used">
                {fmtHours(active.chargeable_hours)} / {fmtHours(active.terms.included_hours)}
              </div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Remaining</div>
              <div className="text-white" data-testid="retainer-remaining">
                {fmtHours(active.remaining_hours)}
              </div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Overage</div>
              <div className={active.overage_hours > 0 ? "text-[#FF6600]" : "text-white"} data-testid="retainer-overage">
                {fmtHours(active.overage_hours)}
              </div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Worked (incl. NC)</div>
              <div className="text-white" data-testid="retainer-worked">
                {fmtHours(active.worked_hours)}
              </div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Included Rate</div>
              <div className="text-white">
                {fmtMoney(active.terms.included_hourly_rate, active.terms.currency)}/hr
              </div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Overage Rate</div>
              <div className="text-white">
                {fmtMoney(active.terms.overage_hourly_rate, active.terms.currency)}/hr
              </div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Included Value</div>
              <div className="text-white" data-testid="retainer-included-value">
                {fmtMoney(active.included_value, active.terms.currency)}
              </div>
            </div>
            <div>
              <div className="text-[#A3A3A3] text-xs">Overage Charges</div>
              <div className="text-white" data-testid="retainer-overage-charges">
                {fmtMoney(active.overage_charges, active.terms.currency)}
              </div>
            </div>
            <div className="col-span-2 mt-1 pt-2 border-t border-[#1F1F1F]">
              <div className="text-[#A3A3A3] text-xs">Current Cycle Total</div>
              <div className="text-white text-base" data-testid="retainer-cycle-total">
                {fmtMoney(active.cycle_total, active.terms.currency)}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-3">
            <button
              data-testid="retainer-close-cycle"
              onClick={closeCycle}
              disabled={busy || alreadyClosed}
              className="ur-btn-secondary text-xs py-2 px-3 disabled:opacity-40"
            >
              {alreadyClosed ? "Cycle Closed" : "Close Cycle"}
            </button>
          </div>

          {/* Contributing Jobs */}
          {(active.contributions || []).length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-[#5A5A5A] mb-2">
                Contributing Jobs
              </div>
              <div className="space-y-1" data-testid="retainer-contributions">
                {active.contributions.map((c) => (
                  <div
                    key={c.job_id}
                    data-testid={`retainer-contribution-${c.job_id}`}
                    className="flex items-center justify-between text-xs bg-[#0F0F0F] border border-[#1F1F1F] rounded px-2 py-1.5"
                  >
                    <div className="truncate flex-1">
                      <span className="text-white">{c.job_title || "(untitled)"}</span>
                      <span className="text-[#5A5A5A] ml-2">
                        {c.received_at ? new Date(c.received_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                      </span>
                    </div>
                    <div className="text-[#A3A3A3]">
                      Worked {fmtHours(c.worked_hours)} · Chargeable {fmtHours(c.chargeable_hours)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-[#A3A3A3]" data-testid="retainer-not-configured">
          Configure Retainer Terms above to enable the cycle summary.
        </div>
      )}

      {/* --- History ---------------------------------------------- */}
      {history.length > 0 && (
        <div className="bg-[#141414] border border-[#1F1F1F] rounded-lg p-3" data-testid="retainer-history">
          <div className="ur-eyebrow mb-2">Retainer History</div>
          <div className="space-y-1">
            {history.map((h) => (
              <div
                key={h.id}
                data-testid={`retainer-history-${h.id}`}
                className="grid grid-cols-4 gap-2 text-xs bg-[#0F0F0F] border border-[#1F1F1F] rounded px-2 py-2"
              >
                <div className="text-white">
                  {fmtDateRange(h.cycle_start, h.cycle_end)}
                </div>
                <div className="text-[#A3A3A3]">Included {fmtHours(h.included_hours)}</div>
                <div className="text-[#A3A3A3]">Chargeable {fmtHours(h.chargeable_hours)}</div>
                <div className={h.overage_hours > 0 ? "text-[#FF6600]" : "text-[#A3A3A3]"}>
                  Overage {fmtHours(h.overage_hours)}
                </div>
                <div className="col-span-4 text-[10px] text-[#5A5A5A]">
                  Total {fmtMoney(h.cycle_total, h.currency)} · closed {new Date(h.closed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RetainerPanel;
